import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Scissors,
  Upload,
  Play,
  Pause,
  CheckCircle,
  Clock,
  Film,
  Sparkles,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Filter,
  X,
} from 'lucide-react';
import {
  analyzeGameVideo,
  formatTimestamp,
  formatDuration,
  groupClipsByType,
  sortClips,
  filterClipsByType,
  PLAY_TYPE_COLORS,
} from '../../services/baseballClippingService';

/**
 * BaseballClipping - AI-Powered Game Clip Extraction
 *
 * Upload game footage and receive AI-segmented clips with play labels.
 * Uses Gemini Vision API for video analysis.
 * Baseball-specific play types: At-Bats, Pitch Sequences, Defensive Plays, etc.
 */

function StepCard({ number, title, description, icon }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-[14px] bg-[#111111]">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
        {number}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          {icon}
          <h4 className="font-semibold text-[#F5F5F5]">
            {title}
          </h4>
        </div>
        <p className="mt-1 text-sm text-[#999999]">
          {description}
        </p>
      </div>
    </div>
  );
}

function UploadZone({ onFileDrop, isUploading, uploadProgress, statusMessage }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFileDrop(files[0]);
    }
  }, [onFileDrop]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      onFileDrop(files[0]);
    }
  }, [onFileDrop]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative p-8 rounded-2xl border-2 border-dashed transition-all cursor-pointer
        ${isDragging
          ? 'border-blue-500 bg-blue-500/10 scale-[1.02]'
          : 'border-[#1A1A1A] bg-[#0A0A0A] hover:border-blue-500/50'
        }
        ${isUploading ? 'pointer-events-none' : ''}
      `}
    >
      <input
        type="file"
        accept="video/mp4,video/quicktime,video/mov,.mp4,.mov"
        onChange={handleFileSelect}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        disabled={isUploading}
      />

      <div className="text-center">
        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
          isDragging ? 'bg-blue-500 text-white' : 'bg-[#1A1A1A]'
        }`}>
          {isUploading ? (
            <RefreshCw className="animate-spin text-blue-500" size={32} />
          ) : (
            <Upload className={isDragging ? 'text-white' : 'text-blue-500'} size={32} />
          )}
        </div>

        <h3 className="text-lg font-semibold mb-2 text-[#F5F5F5]">
          {isUploading ? statusMessage || 'Processing...' : 'Drop your game video here'}
        </h3>

        {isUploading ? (
          <div className="mt-4 max-w-xs mx-auto">
            <div className="h-2 rounded-full overflow-hidden bg-[#1A1A1A]">
              <div
                className="h-full bg-blue-500 transition-all duration-500"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="mt-2 text-sm text-[#555555]">
              {uploadProgress}% complete
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm mb-4 text-[#999999]">
              or click to browse files
            </p>
            <div className="flex items-center justify-center gap-3">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#1A1A1A] text-[#999999]">
                MP4
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#1A1A1A] text-[#999999]">
                MOV
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#1A1A1A] text-[#999999]">
                Up to 2GB
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PlayTypeBadge({ playType }) {
  const colorClass = PLAY_TYPE_COLORS[playType] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${colorClass}`}>
      {playType}
    </span>
  );
}

function ClipCard({ clip, onPlayClip, isActive }) {
  return (
    <button
      onClick={() => onPlayClip(clip)}
      className={`w-full text-left p-4 rounded-[14px] border transition-all ${
        isActive
          ? 'border-blue-500 bg-blue-500/10'
          : 'bg-[#111111] border-[#1A1A1A] hover:border-[#333333]'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <PlayTypeBadge playType={clip.playType} />
        <span className="text-xs text-[#555555]">
          {clip.inning ? `Inning ${clip.inning}` : '--'}
        </span>
      </div>

      <p className="text-sm mb-2 line-clamp-2 text-[#CCCCCC]">
        {clip.description}
      </p>

      <div className="flex items-center justify-between">
        <span className="text-xs text-[#555555]">
          {formatTimestamp(clip.startTime)} - {formatTimestamp(clip.endTime)}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded ${
          clip.confidence >= 0.8
            ? 'bg-green-500/20 text-green-400'
            : clip.confidence >= 0.6
              ? 'bg-[#E8B800]/20 text-[#E8B800]'
              : 'bg-gray-500/20 text-gray-400'
        }`}>
          {Math.round(clip.confidence * 100)}%
        </span>
      </div>

      {clip.outcome && (
        <div className="mt-2 text-xs text-[#555555]">
          Outcome: <span className="capitalize">{clip.outcome}</span>
        </div>
      )}
    </button>
  );
}

function ClipResults({ clips, videoRef, videoUrl }) {
  const [activeClip, setActiveClip] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Group clips by type for filter buttons
  const clipGroups = groupClipsByType(clips);
  const playTypes = Object.keys(clipGroups);

  // Filter clips
  const filteredClips = filterType
    ? filterClipsByType(clips, [filterType])
    : clips;

  // Sort clips by time
  const sortedClips = sortClips(filteredClips, 'time', 'asc');

  const handlePlayClip = useCallback((clip) => {
    setActiveClip(clip);
    if (videoRef.current) {
      videoRef.current.currentTime = clip.startTime;
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, [videoRef]);

  const handleTogglePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [videoRef, isPlaying]);

  // Monitor video time to stop at clip end
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeClip) return;

    const handleTimeUpdate = () => {
      if (video.currentTime >= activeClip.endTime) {
        video.pause();
        setIsPlaying(false);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [videoRef, activeClip]);

  return (
    <div className="rounded-[14px] border bg-[#0A0A0A] border-[#1A1A1A]">
      {/* Header */}
      <div className="p-4 border-b border-[#1A1A1A]">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2 text-[#F5F5F5]">
            <Film className="text-blue-500" size={20} />
            Extracted Plays ({clips.length})
          </h3>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-[#1A1A1A] text-[#CCCCCC] hover:bg-[rgba(255,255,255,0.04)]"
          >
            <Filter size={16} />
            Filter
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>

        {/* Filter pills */}
        {showFilters && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => setFilterType(null)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                filterType === null
                  ? 'bg-blue-500 text-white'
                  : 'bg-[#1A1A1A] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]'
              }`}
            >
              All ({clips.length})
            </button>
            {playTypes.map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  filterType === type
                    ? 'bg-blue-500 text-white'
                    : 'bg-[#1A1A1A] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]'
                }`}
              >
                {type} ({clipGroups[type].length})
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Video Player */}
      {videoUrl && (
        <div className="p-4 border-b border-[#1A1A1A]">
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full"
              controls={false}
            />

            {/* Custom controls overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleTogglePlay}
                    className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white"
                  >
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                  </button>

                  {activeClip && (
                    <span className="text-white text-sm">
                      Playing: {activeClip.playType}
                    </span>
                  )}
                </div>

                {activeClip && (
                  <PlayTypeBadge playType={activeClip.playType} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clips Grid */}
      <div className="p-4 max-h-[400px] overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sortedClips.map(clip => (
            <ClipCard
              key={clip.id}
              clip={clip}
              onPlayClip={handlePlayClip}
              isActive={activeClip?.id === clip.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AnalysisSummary({ summary }) {
  if (!summary || !summary.playTypeCounts) return null;

  const entries = Object.entries(summary.playTypeCounts).sort((a, b) => b[1] - a[1]);

  return (
    <div className="p-4 rounded-[14px] border bg-[#0A0A0A] border-[#1A1A1A]">
      <h4 className="font-semibold mb-3 text-[#F5F5F5]">
        Play Breakdown
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {entries.map(([type, count]) => (
          <div key={type} className="text-center">
            <PlayTypeBadge playType={type} />
            <p className="mt-1 text-lg font-bold text-[#F5F5F5]">
              {count}
            </p>
          </div>
        ))}
      </div>
      {summary.averagePlayDuration > 0 && (
        <p className="mt-3 text-sm text-[#999999]">
          Average play duration: {formatDuration(summary.averagePlayDuration)}
        </p>
      )}
    </div>
  );
}

export default function BaseballClipping() {
  const videoRef = useRef(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState(null);

  // Analysis results
  const [analysisResult, setAnalysisResult] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  // Custom focus prompt for specific play detection
  const [focusPrompt, setFocusPrompt] = useState('');

  const handleFileDrop = useCallback(async (file) => {
    setIsProcessing(true);
    setError(null);
    setUploadProgress(0);
    setStatusMessage('Preparing upload...');
    setAnalysisResult(null);

    // Create object URL for video playback
    const objectUrl = URL.createObjectURL(file);
    setVideoUrl(objectUrl);

    try {
      const result = await analyzeGameVideo(file, {
        onProgress: (progress, message) => {
          setUploadProgress(progress);
          setStatusMessage(message);
        },
        focusPrompt: focusPrompt.trim() || undefined,
      });

      if (result.success) {
        setAnalysisResult(result);
        setStatusMessage(`Found ${result.totalPlays} plays!`);
      } else {
        throw new Error(result.message || 'Analysis failed');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message);
      setStatusMessage('');
      // Clean up video URL on error
      URL.revokeObjectURL(objectUrl);
      setVideoUrl(null);
    } finally {
      setIsProcessing(false);
    }
  }, [focusPrompt]);

  // Cleanup video URL on unmount
  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  const handleReset = useCallback(() => {
    setAnalysisResult(null);
    setError(null);
    setUploadProgress(0);
    setStatusMessage('');
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
      setVideoUrl(null);
    }
  }, [videoUrl]);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Hero Section */}
      <div className="text-center py-6">
        <div className="flex items-center justify-center gap-3 mb-3">
          <div className="p-3 rounded-[14px] bg-blue-500/20">
            <Scissors className="text-blue-500" size={32} />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-[#F5F5F5]">
          Game Clipping
        </h1>
        <p className="mt-2 text-lg text-[#999999]">
          Upload game footage and let AI break it down into individual plays
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 rounded-[14px] bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
          <div className="flex-1">
            <p className="text-red-400 font-medium">Analysis Failed</p>
            <p className="text-red-400/80 text-sm">{error}</p>
          </div>
          <button
            onClick={handleReset}
            className="px-3 py-1 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Show results or upload interface */}
      {analysisResult?.clips?.length > 0 ? (
        <>
          {/* Success Header */}
          <div className="p-4 rounded-[14px] border flex items-center justify-between bg-green-500/10 border-green-500/30">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-500" size={24} />
              <div>
                <p className="font-semibold text-green-400">
                  Analysis Complete!
                </p>
                <p className="text-sm text-green-400/70">
                  Found {analysisResult.totalPlays} plays in {formatDuration(analysisResult.processingTime / 1000)}
                </p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-[#1A1A1A] text-[#CCCCCC] hover:bg-[rgba(255,255,255,0.04)]"
            >
              Analyze Another
            </button>
          </div>

          {/* Summary */}
          <AnalysisSummary summary={analysisResult.summary} />

          {/* Clip Results */}
          <ClipResults
            clips={analysisResult.clips}
            videoRef={videoRef}
            videoUrl={videoUrl}
          />
        </>
      ) : (
        <>
          {/* How It Works */}
          <div className="p-6 rounded-[14px] border bg-[#0A0A0A] border-[#1A1A1A]">
            <h2 className="text-lg font-semibold mb-4 text-[#F5F5F5]">
              How It Works
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StepCard
                number={1}
                title="Upload"
                description="Drop your game video here. We accept MP4 and MOV files up to 2GB."
                icon={<Upload className="text-blue-500" size={18} />}
              />
              <StepCard
                number={2}
                title="AI Analysis"
                description="Gemini Vision watches the footage and identifies every play automatically."
                icon={<Sparkles className="text-blue-500" size={18} />}
              />
              <StepCard
                number={3}
                title="Get Clips"
                description="View plays with timestamps, type labels, and AI descriptions."
                icon={<Film className="text-blue-500" size={18} />}
              />
            </div>

            <div className="mt-4 p-3 rounded-lg flex items-center gap-3 bg-blue-500/10 border border-blue-500/20">
              <Clock className="text-blue-500 flex-shrink-0" size={20} />
              <p className="text-sm text-blue-300">
                <strong>Processing time:</strong> Typically 1-3 minutes per inning, depending on video length.
              </p>
            </div>
          </div>

          {/* Optional Focus Prompt */}
          <div className="p-5 rounded-[14px] border bg-[#0A0A0A] border-[#1A1A1A]">
            <label className="block text-sm font-medium mb-2 text-[#CCCCCC]">
              <Sparkles className="inline-block mr-2 text-blue-500" size={16} />
              Focus Area (Optional)
            </label>
            <input
              type="text"
              value={focusPrompt}
              onChange={(e) => setFocusPrompt(e.target.value)}
              placeholder="e.g., Strikeouts only, Double plays, Player #23 at-bats..."
              disabled={isProcessing}
              className="w-full px-4 py-3 rounded-lg border transition-colors bg-[#111111] border-[#1A1A1A] text-[#F5F5F5] placeholder-[#555555] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50"
            />
            <p className="mt-2 text-xs text-[#555555]">
              Leave blank for general play detection, or specify what to focus on.
            </p>
          </div>

          {/* Upload Zone */}
          <UploadZone
            onFileDrop={handleFileDrop}
            isUploading={isProcessing}
            uploadProgress={uploadProgress}
            statusMessage={statusMessage}
          />

          {/* Tips for Best Results */}
          <div className="p-5 rounded-[14px] border bg-[#0A0A0A] border-[#1A1A1A]">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-[#F5F5F5]">
              <AlertCircle className="text-blue-500" size={20} />
              Tips for Best Results
            </h3>

            <ul className="space-y-2 text-sm text-[#999999]">
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span><strong>Center field view</strong> works best - behind home plate or press box angles are ideal</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span><strong>One inning at a time</strong> - split full games into innings for faster processing</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span><strong>Good lighting</strong> - day games or well-lit night games work best</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span><strong>Steady camera</strong> - fixed broadcast or tripod footage is preferred</span>
              </li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
